import { useState } from "react";
import { Flame, ExternalLink, X, Mail } from "lucide-react";
import { toast } from "sonner";
import { createPledge } from "@/lib/api";
import { getSource } from "@/lib/source";
import { PINKSALE_URL } from "@/lib/links";

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// Post-launch "Buy" button that captures an email before sending the user to
// PinkSale — so even ready-to-buy visitors land on the waitlist for updates.
export const BuyPinkSaleButton = ({
  className,
  label = "Buy $AFTBRN on PinkSale",
  testId = "buy-pinksale-btn",
  onNavigate,
}) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const goToPinkSale = () => {
    window.open(PINKSALE_URL, "_blank", "noopener,noreferrer");
    setOpen(false);
    setEmail("");
    if (onNavigate) onNavigate();
  };

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (value && isValidEmail(value)) {
      setSubmitting(true);
      try {
        await createPledge({ name: value.split("@")[0], email: value, amount: 0, source: getSource() });
        toast.success("You're on the list — taking you to PinkSale.");
      } catch {
        // Never block a buyer if capture fails (e.g. duplicate email).
      } finally {
        setSubmitting(false);
      }
    }
    goToPinkSale();
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testId} className={className}>
        <Flame className="w-4 h-4" />
        {label}
        <ExternalLink className="w-4 h-4" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          data-testid="buy-capture-modal"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative w-full max-w-md bg-card border border-ember/40 p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="buy-capture-close"
              aria-label="Close"
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 text-ember mb-3">
              <Flame className="w-5 h-5" />
              <span className="font-mono text-xs uppercase tracking-[0.2em]">Before you buy · $AFTBRN</span>
            </div>
            <h3 className="font-display font-bold text-2xl">Get launch & impact updates</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Drop your email and we'll keep you posted on presale news and reforestation milestones. Then we'll send you straight to PinkSale.
            </p>
            <form onSubmit={submit} noValidate className="mt-6 space-y-4" data-testid="buy-capture-form">
              <div className="relative">
                <Mail className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  data-testid="buy-capture-email-input"
                  className="w-full bg-background border-b border-muted-foreground/60 pl-10 pr-4 py-3 focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/50"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="buy-capture-continue-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-ember text-black font-bold py-3.5 hover:bg-ember-light transition-colors disabled:opacity-60"
              >
                {submitting ? "Saving..." : "Continue to PinkSale"}
                <ExternalLink className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={goToPinkSale}
                data-testid="buy-capture-skip-btn"
                className="w-full text-center font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
              >
                No thanks, take me to PinkSale
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
